import { useCallback, useMemo } from "react";
import type { IconName } from "@salestrak/ui";
import type {
  NotificationTypeBucket,
  NotificationWhereInput,
} from "#app/lib/data";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";

/** The inbox filter selection. Extends the server's type-bucket enum with the
 *  client-only `UNREAD` read-state filter (written as an extension so it can't
 *  drift from the enum). Single-select — one filter applies at a time. */
export type InboxFilter = NotificationTypeBucket | "UNREAD";

export interface InboxFilterDef {
  key: InboxFilter;
  label: string;
  icon: IconName;
  /** Digit for the Shift+N hotkey (1–3), or null for filters with no hotkey. */
  hotkey: 1 | 2 | 3 | null;
}

/** Menu order matches the reference: the three hotkeyed filters. (Reminders is
 *  omitted until the product grows a reminder concept; Updates was offered and
 *  cut — Spencer, 2026-08-18 — the bucket stays on the server enum only.) */
export const INBOX_FILTERS: InboxFilterDef[] = [
  { key: "MENTION", label: "Mentions", icon: "at-sign", hotkey: 1 },
  { key: "ASSIGNMENT", label: "Assigned to me", icon: "circle-user", hotkey: 2 },
  { key: "UNREAD", label: "Unread", icon: "mail", hotkey: 3 },
];

const FILTER_BY_KEY = new Map(INBOX_FILTERS.map((f) => [f.key, f]));

export function inboxFilterLabel(filter: InboxFilter): string {
  return FILTER_BY_KEY.get(filter)?.label ?? "Filter";
}

/** The server `where` for a selected filter. UNREAD reads unread rows; every
 *  other key is a derived type bucket. */
export function inboxFilterWhere(filter: InboxFilter): NotificationWhereInput {
  return filter === "UNREAD" ? { read: false } : { type: filter };
}

/** Which filters apply on a given tab. Unread is a live-triage filter with no
 *  meaning on the Cleared (already-handled) tab, so it is offered only on All;
 *  a disabled item there explains itself with a tooltip. */
export function isInboxFilterAvailable(
  filter: InboxFilter,
  tab: "all" | "cleared",
): boolean {
  if (tab === "cleared" && filter === "UNREAD") return false;
  return true;
}

export type InboxOrdering = "newest" | "oldest";

interface InboxViewState {
  ordering: InboxOrdering;
  filter: InboxFilter | null;
}

const INBOX_VIEW_DEFAULTS: InboxViewState = { ordering: "newest", filter: null };

// ONE per-browser key, not per workspace: the filter and ordering describe
// how the viewer reads their inbox, not which workspace it belongs to.
const INBOX_VIEW_KEY = "inbox.view";

/** The inbox's filter and ordering, persisted per browser so a reload lands
 *  on the same view. A stored value the current build doesn't know (a filter
 *  key that was since cut) falls back to the default rather than sending the
 *  server an unknown where-clause. */
export function useInboxViewState(): {
  ordering: InboxOrdering;
  setOrdering: (ordering: InboxOrdering) => void;
  filter: InboxFilter | null;
  setFilter: (
    filter: InboxFilter | null | ((current: InboxFilter | null) => InboxFilter | null),
  ) => void;
} {
  const { state, update } = usePersistedState(INBOX_VIEW_KEY, INBOX_VIEW_DEFAULTS);
  const ordering: InboxOrdering = state.ordering === "oldest" ? "oldest" : "newest";
  const filter =
    state.filter !== null && FILTER_BY_KEY.has(state.filter) ? state.filter : null;
  const setOrdering = useCallback(
    (next: InboxOrdering) => update({ ordering: next }),
    [update],
  );
  const setFilter = useCallback(
    (next: InboxFilter | null | ((current: InboxFilter | null) => InboxFilter | null)) =>
      update((current) => ({
        filter: typeof next === "function" ? next(current.filter) : next,
      })),
    [update],
  );
  return useMemo(
    () => ({ ordering, setOrdering, filter, setFilter }),
    [ordering, setOrdering, filter, setFilter],
  );
}
